import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { HeartHandshake, CheckCircle2 } from "lucide-react";
import { Bullets, Reveal } from "@/components/shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VolunteerSection({ branchName, accent }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/volunteers`, { ...form, branch: branchName });
      setSent(true);
      toast.success("Volunteer registration received. We'll be in touch.");
    } catch {
      toast.error("Could not register right now. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-md border border-white/15 bg-[#0A0D14]/80 px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:border-amber-500/60 focus:outline-none focus:ring-1 focus:ring-amber-500/40 transition-colors";

  return (
    <section data-testid="volunteer-section" className="max-w-7xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24">
      <Reveal>
        <div
          className="card-pain relative overflow-hidden p-6 sm:p-10 grid gap-10 lg:grid-cols-2"
          style={{ borderColor: `${accent}66` }}
        >
          <div
            className="absolute inset-x-0 top-0 h-[2px]"
            style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }}
          />
          <div>
            <HeartHandshake className="h-8 w-8" style={{ color: accent }} />
            <h2 className="font-display mt-4 text-2xl sm:text-3xl font-semibold tracking-tight text-slate-50">
              Volunteer with {branchName}
            </h2>
            <div
              className="h-[2px] w-16 my-4"
              style={{ background: `linear-gradient(90deg, ${accent}, transparent)` }}
            />
            <Bullets
              items={[
                "No experience needed — full guidance and training provided",
                "Choose your own hours and commitment level",
                "Every volunteer is vetted, supported, and never sent alone",
                "You join a team that shows up — on the street, in court, and in the community",
              ]}
            />
          </div>
          {sent ? (
            <div
              data-testid="volunteer-success-panel"
              className="self-center rounded-lg border p-8 text-center"
              style={{ borderColor: `${accent}66`, background: `${accent}14` }}
            >
              <CheckCircle2 className="mx-auto h-10 w-10" style={{ color: accent }} />
              <h3 className="font-display mt-4 text-xl text-slate-50">You're on the team</h3>
              <p className="mt-2 text-sm text-slate-300 leading-relaxed">
                Your registration for {branchName} has been received. A P.A.I.N. coordinator will
                reach out to you directly.
              </p>
            </div>
          ) : (
            <form data-testid="volunteer-form" onSubmit={submit} className="space-y-4 self-center w-full">
              <input
                data-testid="volunteer-name-input"
                className={inputCls}
                placeholder="Full name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              <input
                type="email"
                data-testid="volunteer-email-input"
                className={inputCls}
                placeholder="Email address"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
              <input
                data-testid="volunteer-phone-input"
                className={inputCls}
                placeholder="Phone (optional)"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
              <textarea
                data-testid="volunteer-message-input"
                className={`${inputCls} min-h-[100px] resize-y`}
                placeholder="Availability, skills, or anything you'd like us to know (optional)"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
              />
              <button
                type="submit"
                data-testid="volunteer-submit-button"
                disabled={sending}
                className="btn-royal w-full rounded-md px-6 py-3 text-sm font-semibold text-white disabled:opacity-60"
              >
                {sending ? "Registering..." : `Register as a ${branchName} Volunteer`}
              </button>
            </form>
          )}
        </div>
      </Reveal>
    </section>
  );
}
